import {
  CROP_GROW_TIMES, CROP_GROW_TIMES_BY_TYPE, CROP_MAX_STAGE, ENERGY_COST,
  FIELD_DRY_TIME, SEED_COSTS, SEED_MIN_PER_TILE, SEED_DUD_TIMEOUT,
  SeedType, SimState, WATER_EVAPORATE_TIME, XP_REWARDS,
} from './types';
import { EnergySystem } from './EnergySystem';
import { ProgressionSystem } from './ProgressionSystem';
import { StorageSystem } from './StorageSystem';
import { PlotSystem } from './PlotSystem';
import { EventBus } from './events';

/**
 * Crop growth + plant/water/till/harvest. Operates on plot tile coords.
 * Bulk tools MUST call into these helpers — never duplicate gating elsewhere.
 */
export const CropSystem = {
  /** Per-seed grow times for the given seed type (or the legacy default). */
  growTimes(seed: SeedType): number[] {
    return CROP_GROW_TIMES_BY_TYPE[seed] ?? CROP_GROW_TIMES;
  },

  /** Per-sub-cell crop growth across EVERY adopted plot AND every free
   *  (non-plot) sub-cell. Each seed grows independently; germination
   *  requires ≥ SEED_MIN_PER_TILE seeds on the same tile; watered sub-cells
   *  grow 1.5× faster. Operates on the same shape regardless of source. */
  tickActivePlot(state: SimState, dt: number, _bus: EventBus) {
    type SeedEntry = {
      c: number; r: number; subC: number; subR: number;
      type: SeedType; stage: number; timer: number;
      ox: number; oz: number; growMul: number;
    };
    const tickContainer = (container: {
      tilled: any[]; seeds: SeedEntry[]; wet: { c: number; r: number; subC: number; subR: number; stage: number; age?: number }[];
    }) => {
      // Water evaporation — each wet entry ages out one stage per
      // WATER_EVAPORATE_TIME seconds. Fully-evaporated entries are removed.
      for (let i = container.wet.length - 1; i >= 0; i--) {
        const w = container.wet[i];
        w.age = (w.age ?? 0) + dt;
        if (w.age >= WATER_EVAPORATE_TIME) {
          w.age -= WATER_EVAPORATE_TIME;
          w.stage--;
          if (w.stage <= 0) container.wet.splice(i, 1);
        }
      }
      const counts = new Map<string, number>();
      for (const s of container.seeds) {
        const k = `${s.c}:${s.r}`;
        counts.set(k, (counts.get(k) ?? 0) + 1);
      }
      // Under-seeded sub-cell cleanup. Seeds on a tile that has fewer than
      // SEED_MIN_PER_TILE neighbours stay at stage 0 forever — they should
      // wither off the tile after SEED_DUD_TIMEOUT so the player can re-use
      // the sub-cell without accumulating dead stalks.
      for (let i = container.seeds.length - 1; i >= 0; i--) {
        const seed = container.seeds[i] as typeof container.seeds[0] & { dudAge?: number };
        if (seed.stage > 0) { seed.dudAge = 0; continue; }
        const tileCount = counts.get(`${seed.c}:${seed.r}`) ?? 0;
        if (tileCount >= SEED_MIN_PER_TILE) { seed.dudAge = 0; continue; }
        seed.dudAge = (seed.dudAge ?? 0) + dt;
        if (seed.dudAge >= SEED_DUD_TIMEOUT) {
          container.seeds.splice(i, 1);
          counts.set(`${seed.c}:${seed.r}`, Math.max(0, (counts.get(`${seed.c}:${seed.r}`) ?? 0) - 1));
        }
      }
      // Pre-index wet entries by sub-cell key — turns the O(N) .find()
      // inside the per-seed growth loop into O(1) lookup. With hundreds of
      // seeds and dozens of wet patches per plot this was a per-frame hot
      // path; indexing once amortizes the scan across all seeds.
      const wetByKey = new Map<string, number>();    // value = stage
      for (const w of container.wet) {
        if (w.stage >= 1) wetByKey.set(`${w.c}:${w.r}:${w.subC}:${w.subR}`, w.stage);
      }
      for (const seed of container.seeds) {
        if (seed.stage >= CROP_MAX_STAGE) continue;
        const tileCount = counts.get(`${seed.c}:${seed.r}`) ?? 0;
        if (tileCount < SEED_MIN_PER_TILE) continue;
        const wetMul = wetByKey.has(`${seed.c}:${seed.r}:${seed.subC}:${seed.subR}`) ? 1.5 : 1.0;
        const speed = wetMul * (seed.growMul ?? 1.0);
        seed.timer -= dt * speed;
        if (seed.timer <= 0) {
          seed.stage++;
          const times = CROP_GROW_TIMES_BY_TYPE[seed.type] ?? CROP_GROW_TIMES;
          seed.timer = times[Math.min(seed.stage, times.length - 1)];
        }
      }
    };
    if (Array.isArray(state.plots)) {
      for (const plot of state.plots) tickContainer(plot);
    }
    // Free-tile sub-cell ticking — same logic, world-level arrays.
    if (Array.isArray(state.freeTilled) && Array.isArray(state.freeSeeds) && Array.isArray(state.freeWet)) {
      tickContainer({ tilled: state.freeTilled, seeds: state.freeSeeds, wet: state.freeWet });
    }
  },

  tick(state: SimState, dt: number, bus: EventBus) {
    PlotSystem.forEachTile(state, (tile, c, r) => {
      const cr = tile.crop;
      if (cr && cr.stage < CROP_MAX_STAGE) {
        const speed = tile.watered ? 1.5 : 1.0;
        cr.timer -= dt * speed;
        if (cr.timer <= 0) {
          cr.stage++;
          const times = CROP_GROW_TIMES_BY_TYPE[cr.type] ?? CROP_GROW_TIMES;
          cr.timer = times[Math.min(cr.stage, times.length - 1)];
        }
      }
      // Drying: an empty tilled tile reverts to grass if neglected. Watering
      // refreshes the timer; planting clears it (the crop owns the tile).
      if (tile.surf === 'field' && !cr && tile.surfTimer !== undefined) {
        tile.surfTimer -= dt;
        if (tile.surfTimer <= 0) {
          tile.surf = 'grass';
          tile.watered = false;
          delete tile.surfTimer;
          bus.emit({ type: 'fieldDried', col: c, row: r });
        }
      }
    });
  },

  // ── Till grass → field (hoe) ──────────────────────────────────────
  canPlaceField(state: SimState, c: number, r: number): boolean {
    const t = PlotSystem.tile(state, c, r);
    if (!t) return false;
    if (t.bld) return false;
    if (t.surf !== 'grass') return false;
    if (t.crop) return false;
    if (t.thing) return false;
    return EnergySystem.has(state, ENERGY_COST.placeField);
  },

  placeField(state: SimState, c: number, r: number, bus: EventBus): boolean {
    if (!this.canPlaceField(state, c, r)) {
      const t = PlotSystem.tile(state, c, r);
      if (t?.thing)        bus.emit({ type: 'blocked', reason: 'Clear it first (use the axe).' });
      else if (t?.surf === 'road') bus.emit({ type: 'blocked', reason: 'Can\'t till the road.' });
      else if (!EnergySystem.has(state, ENERGY_COST.placeField))
                            bus.emit({ type: 'blocked', reason: 'Not enough energy' });
      else                  bus.emit({ type: 'blocked', reason: 'Cannot till here' });
      return false;
    }
    EnergySystem.spend(state, ENERGY_COST.placeField);
    const t = state.plot.tiles[r][c];
    t.surf = 'field';
    t.surfTimer = FIELD_DRY_TIME;
    ProgressionSystem.addXp(state, XP_REWARDS.placeField, bus);
    bus.emit({ type: 'fieldPlaced', col: c, row: r });
    return true;
  },

  // ── Water ─────────────────────────────────────────────────────────
  canWater(state: SimState, c: number, r: number): boolean {
    const t = PlotSystem.tile(state, c, r);
    if (!t) return false;
    if (t.surf !== 'field') return false;
    if (t.watered) return false;
    return state.res.energy >= ENERGY_COST.water;
  },

  water(state: SimState, c: number, r: number, bus: EventBus): boolean {
    const t = PlotSystem.tile(state, c, r);
    if (!t) return false;
    if (t.surf !== 'field') {
      bus.emit({ type: 'blocked', reason: 'Water needs a tilled field tile' });
      return false;
    }
    if (t.watered) {
      bus.emit({ type: 'hint', text: 'Tile already watered.' });
      return false;
    }
    if (!EnergySystem.spend(state, ENERGY_COST.water)) {
      bus.emit({ type: 'blocked', reason: 'Not enough energy' });
      return false;
    }
    t.watered = true;
    // A fresh watering also resets the dry-out timer on an empty field tile,
    // so the player can keep a tile prepared without sowing immediately.
    if (!t.crop) t.surfTimer = FIELD_DRY_TIME;
    ProgressionSystem.addXp(state, XP_REWARDS.water, bus);
    bus.emit({ type: 'watered', col: c, row: r });
    return true;
  },

  // ── Plant seeds ───────────────────────────────────────────────────
  seedCost(seed: SeedType): number { return SEED_COSTS[seed]; },

  canPlant(state: SimState, c: number, r: number): boolean {
    const t = PlotSystem.tile(state, c, r);
    if (!t) return false;
    if (t.bld || t.crop) return false;
    if (t.surf !== 'field') return false;
    if ((state.res.seeds[state.selectedSeed] ?? 0) <= 0) return false;
    return EnergySystem.has(state, ENERGY_COST.plant);
  },

  plant(state: SimState, c: number, r: number, bus: EventBus): boolean {
    const seed = state.selectedSeed;
    const t = PlotSystem.tile(state, c, r);
    if (!t) return false;
    if (t.crop)             { bus.emit({ type: 'blocked', reason: 'Already a crop here' }); return false; }
    if (t.surf !== 'field') { bus.emit({ type: 'blocked', reason: 'Need a tilled field first' }); return false; }
    if ((state.res.seeds[seed] ?? 0) <= 0) {
      bus.emit({ type: 'blocked', reason: `No ${seed} seeds — order some at the mailbox.` });
      return false;
    }
    if (!EnergySystem.has(state, ENERGY_COST.plant)) {
      bus.emit({ type: 'blocked', reason: 'Not enough energy' });
      return false;
    }
    EnergySystem.spend(state, ENERGY_COST.plant);
    state.res.seeds[seed]--;
    const times = CROP_GROW_TIMES_BY_TYPE[seed] ?? CROP_GROW_TIMES;
    t.crop = { type: seed, stage: 0, timer: times[0] };
    delete t.surfTimer;          // crop owns the tile now; no more drying
    ProgressionSystem.addXp(state, XP_REWARDS.plant, bus);
    bus.emit({ type: 'planted', col: c, row: r });
    return true;
  },

  // ── Harvest ───────────────────────────────────────────────────────
  canHarvest(state: SimState, c: number, r: number): boolean {
    const t = PlotSystem.tile(state, c, r);
    if (!t || !t.crop) return false;
    if (t.crop.stage < CROP_MAX_STAGE) return false;
    if (StorageSystem.remaining(state) <= 0) return false;
    return EnergySystem.has(state, ENERGY_COST.harvest);
  },

  harvest(state: SimState, c: number, r: number, bus: EventBus): boolean {
    const t = PlotSystem.tile(state, c, r);
    if (!t || !t.crop || t.crop.stage < CROP_MAX_STAGE) {
      bus.emit({ type: 'blocked', reason: 'Not ready to harvest' });
      return false;
    }
    if (!EnergySystem.has(state, ENERGY_COST.harvest)) {
      bus.emit({ type: 'blocked', reason: 'Not enough energy' });
      return false;
    }
    if (StorageSystem.remaining(state) <= 0) {
      bus.emit({ type: 'blocked', reason: 'Storage full — sell or build a Granary!' });
      return false;
    }
    EnergySystem.spend(state, ENERGY_COST.harvest);
    const cropType = t.crop.type;
    const drop = StorageSystem.deposit(state, c, r, cropType);
    if (!drop.ok) {
      state.res.energy += ENERGY_COST.harvest;
      bus.emit({ type: 'blocked', reason: 'Storage full' });
      return false;
    }
    t.crop = null;
    t.watered = false;
    t.surf = 'field';
    ProgressionSystem.addXp(state, XP_REWARDS.harvest, bus);
    bus.emit({
      type: 'harvested',
      col: c, row: r,
      cropType,
      toGranary: drop.toGranary,
      granaryC: drop.granaryC,
      granaryR: drop.granaryR,
    });
    return true;
  },

  // ── Bulk operations — scoped to the ACTIVE plot only ─────────────
  // Iterating every tile in the world would burn through seeds + energy on
  // far-away grass that the player has never touched. If no plot is active
  // (overview, or no plots adopted yet), bail with a hint.
  bulkPlant(state: SimState, bus: EventBus): number {
    const idx = state.activePlotIdx;
    const plot = (idx != null && idx >= 0) ? state.plots?.[idx] : null;
    if (!plot) {
      bus.emit({ type: 'blocked', reason: 'Open a plot first — bulk plant only works inside the active plot.' });
      return 0;
    }
    let n = 0;
    for (const { c, r } of plot.tiles) {
      if (this.canPlant(state, c, r) && this.plant(state, c, r, bus)) n++;
    }
    if (n === 0) bus.emit({ type: 'blocked', reason: 'Nothing to plant — till a field and order seeds.' });
    return n;
  },

  bulkHarvest(state: SimState, bus: EventBus): number {
    const idx = state.activePlotIdx;
    const plot = (idx != null && idx >= 0) ? state.plots?.[idx] : null;
    if (!plot) {
      bus.emit({ type: 'blocked', reason: 'Open a plot first — bulk harvest only works inside the active plot.' });
      return 0;
    }
    let n = 0;
    for (const { c, r } of plot.tiles) {
      if (this.canHarvest(state, c, r) && this.harvest(state, c, r, bus)) n++;
    }
    if (n === 0) bus.emit({ type: 'blocked', reason: 'Nothing ready to harvest' });
    return n;
  },
};
